/**
 * Rating — THE display primitive for a rating figure.
 *
 * One shape, everywhere a rating is *read*: `X.X★` — the number in Geist Mono
 * with tabular-nums, the star glyph in `var(--accent)`.
 *
 * Design refs:
 *   design.md §6.8  — "`X.X★` in Geist Mono with the accent-coloured star glyph
 *                      matching the attempts-strip (§8.3) and variation-row
 *                      (§10.2) treatments." This is the canonical wording; §8.3
 *                      and §10.2 defer to it.
 *   design.md §10.2 — variation row: `N cooks · last X.X★ · cooked Nd ago`.
 *   design.md §13.2 — Share hero attribution: `by @author · go-to · N cooks · X.X★`.
 *   design.md §13.6 — Share recent-cooks row: `… 4.5★  go-to`.
 *   design.md §2    — one accent, reserved for signal; tabular-nums on every
 *                     numeric; mono for data only; no shadows; no decoration.
 *
 * Why a primitive: before this landed the same figure rendered four different
 * ways across five surfaces (`5.0/5`, `★ 4`, an accent progress bar + `5 / 5`,
 * `4/5`, `5★`). A rating is one datum with one canonical shape; every *display*
 * site now reads it from here.
 *
 * Scope — DISPLAY ONLY. The interactive star inputs (`StarsInput`, the
 * log-a-cook capture sheet, the per-section Rate chip) are separate primitives:
 * they are controls, not readouts, and they keep their own affordance shapes.
 *
 * Formatting: always one decimal (`5` → `5.0`, `4.47` → `4.5`). A rating is a
 * measurement; a fixed decimal keeps a column of them aligned under tabular-nums
 * and stops `5★` / `5.0★` drifting apart across surfaces.
 *
 * "Instrument" is the internal aesthetic name — it NEVER appears in runtime
 * strings, classNames, analytics events, or docs the user sees.
 */

import type { CSSProperties } from "react";

// ── Formatting ────────────────────────────────────────────────────────────────

/**
 * Format a rating value for display: always one decimal place.
 *
 * `5` → `"5.0"`, `4.5` → `"4.5"`, `4.47` → `"4.5"`.
 *
 * Non-finite input clamps to `"0.0"` (defensive — the caller should not render a
 * Rating at all when it has no value; see the `> 0` gates at every call site).
 */
export function formatRatingValue(value: number): string {
  if (!Number.isFinite(value)) return "0.0";
  return value.toFixed(1);
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface RatingProps {
  /** The rating figure, on the scale named by `outOf` (default 5). */
  value: number;
  /**
   * The scale's maximum. Only used for the accessible label (`4.5 out of 5`);
   * the visible glyph is always the single `★`. Defaults to 5 (the `/5`
   * shorthand baked into the grammar — CLAUDE.md §11).
   */
  outOf?: number;
  /**
   * `default` → number in `var(--text)`. `muted` → number in `var(--muted)`,
   * for secondary/metadata rows (feed card, ledger card meta line).
   * The star stays `var(--accent)` in both — it is the signal.
   */
  tone?: "default" | "muted";
  /** Font size. Defaults to `inherit` so the primitive adopts its row's scale. */
  fontSize?: string | number;
  /** Extra style, merged last (font-family / tabular-nums are not overridable). */
  style?: CSSProperties;
  /** Optional class hook for surfaces that style via a stylesheet. */
  className?: string;
  /** `data-testid` on the root. Defaults to `rating`. */
  testId?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * @example
 * <Rating value={4.5} />            // → 4.5★  (text ink, accent star)
 * <Rating value={5} tone="muted" /> // → 5.0★  (muted ink, accent star)
 */
export function Rating({
  value,
  outOf = 5,
  tone = "default",
  fontSize = "inherit",
  style,
  className,
  testId = "rating",
}: RatingProps) {
  const formatted = formatRatingValue(value);

  const rootStyle: CSSProperties = {
    fontFamily: "var(--font-mono)",
    fontVariantNumeric: "tabular-nums",
    fontFeatureSettings: '"tnum"',
    color: tone === "muted" ? "var(--muted)" : "var(--text)",
    fontSize,
    whiteSpace: "nowrap",
    ...style,
  };

  return (
    <span
      role="img"
      aria-label={`${formatted} out of ${outOf}`}
      data-testid={testId}
      data-numeric
      data-rating={formatted}
      {...(className !== undefined ? { className } : {})}
      style={rootStyle}
    >
      {formatted}
      {/* The one accent site on a rating (design.md §6.8). Never a bar, never a
          row of five glyphs — those are input affordances, not readouts. */}
      <span aria-hidden="true" style={{ color: "var(--accent)" }}>
        ★
      </span>
    </span>
  );
}
